import React, { useState, useEffect, useCallback } from 'react'
import {
  withStyles,
  Typography,
  Button,
  LinearProgress,
  Box
} from '@material-ui/core'
import moment from 'moment'
import Error from '@material-ui/icons/Error'
import Tooltip from '@material-ui/core/Tooltip'
import clsx from 'clsx'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import IconButton from '@material-ui/core/IconButton'
import Collapse from '@material-ui/core/Collapse'
import Cancel from '@material-ui/icons/Cancel'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import WarningIcon from '@material-ui/icons/Warning'
import DocumentValidator from '../../../components/DocumentValidator'
import { Warning } from '@material-ui/icons'

const styles = theme => ({
  flex: {
    display: 'flex',
    alignItems: 'center'
  },
  title: {
    marginTop: theme.spacing(5),
    display: 'flex',
    alignItems: 'center'
  },
  icon: {
    marginLeft: theme.spacing(2),
    width: 20,
    height: 20
  },
  iconSuccess: {
    marginLeft: theme.spacing(2),
    width: 20,
    height: 20,
    color: theme.palette.success.dark
  },
  iconWarning: {
    marginLeft: theme.spacing(2),
    width: 20,
    height: 20,
    color: theme.palette.warning.dark
  },
  iconError: {
    marginLeft: theme.spacing(2),
    width: 20,
    height: 20,
    color: theme.palette.error.dark
  },
  progress: {
    marginBottom: theme.spacing(2)
  },
  iconBtn: {
    marginLeft: theme.spacing(2)
  },
  btn: {
    marginTop: theme.spacing(2)
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest
    })
  },
  expandOpen: {
    transform: 'rotate(180deg)'
  }
})

const identity = a => a

function RequisicaoCard({
  title,
  repository,
  match,
  classes,
  children,
  disabled,
  validData,
  validateData,
  warningMessage = '',
  validateFieldName,
  queryToFindRequisicao,
  ...props
}) {
  const transformQuery = props.transformQuery || identity
  const transformManualFetch = props.transformManualFetch || identity

  const [data, setData] = useState()
  const [state, setState] = useState('loading')
  const [message, setMessage] = useState('')
  const [trigger, setTrigger] = useState(false)
  const [expanded, setExpanded] = React.useState(false)
  const isFalhaConsulta = data?.statusRequisicao === 0

  const fetchData = useCallback(async () => {
    const solicitacaoId = match.params.id
    const query = transformQuery({ solicitacaoId })
    setState('loading')
    const response = await repository.list({
      query: queryToFindRequisicao || query,
      paginate: { order: 'dataRequisicao DESC', limit: 1 },
    })
    if (response.ok) {
      const data = response.data[0]
      if (data) {
        setData(data)
        switch (data.statusRequisicao) {
          case 0:
            setMessage('Falha na consulta')
            setState('error')
            break
          case 1:
            setState('success')
            break
          case 2:
            setState('loading')
            setMessage('Processando')
            break
          default:
            setMessage('Falha na consulta')
            setState('error')
            break
        }
      } else {
        setMessage('Nenhuma consulta encontrada')
        setState('error')
      }
    } else {
      setMessage('Falha na consulta')
      setState('error')
    }
  }, [match.params.id])

  useEffect(() => {
    fetchData()
  }, [trigger])

  const handleManualFetch = async () => {
    const solicitacaoId = match.params.id
    const query = transformManualFetch({ solicitacaoId })
    setState('loading')
    await repository.manualFetch(query)
    setTrigger(!trigger)
  }

  const handleExpandClick = () => {
    setExpanded(!expanded)
  }

  if (validateData) {
    validData = validData && validateData(data)
  }

  const IconToShow = () => {
    if (!validData || state !== 'success') return null
    const status = validData.status
    const dataMessage = validData.message

    switch (status) {
      case 'SUCCESS':
        return (
          <CheckCircleIcon className={classes.iconSuccess} color="inherit" />
        )
      case 'WARNING':
        return (
          <Tooltip placement="top" title={dataMessage}>
            <Warning className={classes.iconWarning} color="inherit" />
          </Tooltip>
        )
      case 'ERROR':
        return (
          <Tooltip placement="top" title={dataMessage}>
            <Error className={classes.iconError} color="inherit" />
          </Tooltip>
        )
      default:
        return (
          <Tooltip placement="top" title={dataMessage}>
            <Error className={classes.iconError} color="inherit" />
          </Tooltip>
        )
    }
  }

  return (
    <div>
      <Typography className={classes.title} variant="h6" gutterBottom>
        {title}
        {(state === 'error' || state === 'loading') && (
          <Tooltip placement="top" title={message}>
            {message === 'Falha na consulta' ? (
              <Cancel className={classes.icon} color="secondary" />
            ) : (
              <Error className={classes.icon} color="secondary" />
            )}
          </Tooltip>
        )}
        {state === 'success' &&
          (validData ? (
            <IconToShow />
          ) : (
            <Tooltip placement="top" title={warningMessage}>
              <WarningIcon className={classes.iconWarning} color="inherit" />
            </Tooltip>
          ))}
      </Typography>
      {state === 'loading' && <LinearProgress className={classes.progress} />}
      {data && (
        <>
          <Typography className={classes.flex} variant="body2">
            Consulta realizada{' '}
            {data.dataResposta &&
              moment(data.dataResposta).format('DD/MM/YYYY HH:mm')}
            <IconButton
              className={clsx(classes.expand, {
                [classes.expandOpen]: expanded
              })}
              onClick={handleExpandClick}
              aria-expanded={expanded}
              aria-label="show more"
            >
              <ExpandMoreIcon />
            </IconButton>
          </Typography>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            {!!data.erroResposta && (
              <Typography className={classes.flex} variant="body2">
                {data.erroResposta}
              </Typography>
            )}
            {children(data)}
          </Collapse>
        </>
      )}

      <Button
        fullWidth
        className={classes.btn}
        variant="outlined"
        onClick={handleManualFetch}
        disabled={disabled || state === 'loading'}
        color="primary"
      >
        Consultar novamente
      </Button>
      {isFalhaConsulta && (
        <Box py={2}>
          <DocumentValidator
            name={validateFieldName}
            legend="A consulta está válida?"
          />
        </Box>
      )}
    </div>
  )
}

export default withStyles(styles)(RequisicaoCard)
